import { PluginFunction } from 'vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import {
    BarChart,
    CustomChart,
    FunnelChart,
    GaugeChart,
    HeatmapChart,
    LineChart,
    PieChart
} from 'echarts/charts'
import {
    GridComponent,
    LegendComponent,
    PolarComponent,
    TitleComponent,
    ToolboxComponent,
    TooltipComponent,
    VisualMapComponent,
    DataZoomSliderComponent,
    DataZoomComponent,
    DataZoomInsideComponent,
    GraphicComponent,
    MarkLineComponent
} from 'echarts/components'

/**
 * **Conditional CORS fix for images with specific query parameter**
 *
 * We override the `src` property setter on `HTMLImageElement.prototype` to set `crossOrigin = 'anonymous'`
 * only for images whose URLs contain the query parameter `crossorigin=anonymous`.
 *
 * This targets only the images that need this setting, minimizing potential side effects on other images.
 *
 * **Note:** Ensure that the backend provides image URLs with the `?crossorigin=anonymous` query parameter
 * for images that require `crossOrigin` to be set.
 *
 * Reference: https://github.com/apache/echarts/issues/15117
 */
function fixImagesCORS () {
    const originalSrcDescriptor = Object.getOwnPropertyDescriptor(
        HTMLImageElement.prototype,
        'src'
    )

    if (originalSrcDescriptor && originalSrcDescriptor.set) {
        Object.defineProperty(HTMLImageElement.prototype, 'src', {
            set (value: string) {
                if (value && value.includes('crossorigin=anonymous')) {
                    this.crossOrigin = 'anonymous'
                }

                originalSrcDescriptor.set?.call(this, value)
            },
            get: originalSrcDescriptor.get,
            configurable: true,
            enumerable: true,
        })
    } else {
        // Fallback in case the descriptor is not available
        const originalSetAttribute = HTMLImageElement.prototype.setAttribute

        HTMLImageElement.prototype.setAttribute = function (
            name: string,
            value: string
        ) {
            if (name === 'src' && value && value.includes('crossorigin=anonymous')) {
                this.crossOrigin = 'anonymous'
            }
            return originalSetAttribute.call(this, name, value)
        }
    }
}

const install: PluginFunction<never> = (Vue) => {
    Vue.component('VChart', VChart)

    fixImagesCORS()

    use([
        CanvasRenderer, // Beware that changing the renderer to SVG will break the export functionality, only canvas renderer allows to use
        BarChart,
        FunnelChart,
        GaugeChart,
        LineChart,
        CustomChart,
        TitleComponent,
        ToolboxComponent,
        TooltipComponent,
        LegendComponent,
        GridComponent,
        HeatmapChart,
        VisualMapComponent,
        PolarComponent,
        DataZoomSliderComponent,
        GraphicComponent,
        PieChart,
        DataZoomComponent,
        DataZoomInsideComponent,
        MarkLineComponent
    ])
}

export default {
    install
}
